import { Component } from "@theme/component";
import {
  debounce,
  onDocumentLoaded,
  setHeaderMenuStyle,
} from "@theme/utilities";
import { MegaMenuHoverEvent } from "@theme/events";

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ["overflowMenu"];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  /**
   * Tracks the active submenu's size so the underlay card always fits the
   * currently visible content (panel switches, image loads, responsive, etc.).
   * @type {ResizeObserver | null}
   */
  #submenuResizeObserver = null;

  /**
   * Whether the menu opens on click (set in the theme editor) instead of hover.
   * @type {boolean}
   */
  #clickToOpen = false;

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener("resize", this.#resizeListener);

    this.#clickToOpen = this.dataset.menuTrigger === "click";

    if (this.#clickToOpen) {
      this.addEventListener("click", this.#onClick);
      document.addEventListener("click", this.#onDocumentClick);
      document.addEventListener("keydown", this.#onKeydown);
    } else {
      this.overflowMenu?.addEventListener(
        "pointerleave",
        this.#overflowSubmenuListener,
      );
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener("resize", this.#resizeListener);
    clearTimeout(this.#closeTimer);
    document.body.removeEventListener("pointermove", this.#onPointerMove);
    if (this.#state.activeItem) {
      this.#stopPointerTracking(this.#state.activeItem);
    }
    this.removeEventListener("click", this.#onClick);
    document.removeEventListener("click", this.#onDocumentClick);
    document.removeEventListener("keydown", this.#onKeydown);
    this.overflowMenu?.removeEventListener(
      "pointerleave",
      this.#overflowSubmenuListener,
    );
    this.#cleanupMutationObserver();
    this.#submenuResizeObserver?.disconnect();
    this.#submenuResizeObserver = null;
  }

  /**
   * Click-to-open mode: toggle a menu item's submenu on click. Items without a
   * submenu navigate normally; links inside an open submenu are left untouched.
   * @param {MouseEvent} event
   */
  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const link = /** @type {HTMLElement | null} */ (
      event.target.closest('[ref="menuitem"]')
    );
    if (!link) return;

    const item = /** @type {HTMLElement | null} */ (
      link.closest(".menu-list__list-item")
    );
    if (!item || !findSubmenu(link)) return;

    event.preventDefault();

    if (this.#state.activeItem === link) {
      this.#deactivate(link);
    } else {
      this.activate(/** @type {any} */ ({ target: item, type: "click" }));
    }
  };

  /**
   * Click-to-open mode: close the open submenu when clicking outside the menu.
   * @param {MouseEvent} event
   */
  #onDocumentClick = (event) => {
    if (!this.#state.activeItem) return;
    if (event.target instanceof Node && this.contains(event.target)) return;
    this.#deactivate();
  };

  /**
   * Click-to-open mode: close the open submenu on Escape.
   * @param {KeyboardEvent} event
   */
  #onKeydown = (event) => {
    if (event.key === "Escape" && this.#state.activeItem) {
      this.#deactivate();
    }
  };

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);

  #overflowSubmenuListener = () => {
    this.#deactivate();
  };

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #pointerIdleTimer;

  /**
   * Grace-period timer that delays closing the submenu on pointer-out, so slow
   * movement across the gap between a menu item and its submenu doesn't close it.
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #closeTimer;

  /**
   * Last known pointer position for Safari hit-test reconciliation.
   * @type {{ x: number, y: number }}
   */
  #lastPointer = { x: 0, y: 0 };

  /**
   * Update the safety box idle state on the active menu item.
   * @param {PointerEvent} event
   */
  #onPointerMove = (event) => {
    const activeLink = this.#state.activeItem;
    if (!activeLink) return;

    this.#lastPointer.x = event.clientX;
    this.#lastPointer.y = event.clientY;

    // The safety box is a full-width overlay that keeps the current submenu open
    // while the pointer travels down into it. Only raise it for predominantly
    // DOWNWARD motion (toward the open submenu). Horizontal/upward motion means
    // the user is browsing sibling top-level items, so keep the box down and let
    // their `pointerenter` fire immediately — otherwise the overlay blocks the
    // sibling unless the pointer happens to go idle, which is why the menu only
    // opened when you moved fast.
    const movingToSubmenu =
      event.movementY >= 1 && event.movementY >= Math.abs(event.movementX);
    activeLink.dataset.safetyBox = `${movingToSubmenu}`;

    clearTimeout(this.#pointerIdleTimer);
    if (movingToSubmenu) {
      this.#pointerIdleTimer = setTimeout(() => {
        if (this.#state.activeItem) {
          this.#state.activeItem.dataset.safetyBox = "false";
          this.#reconcilePointerTarget();
        }
      }, 50);
    } else {
      this.#reconcilePointerTarget();
    }
  };

  /**
   * Check if the pointer is over a different menu item and trigger activation if so.
   * Works around Safari not re-evaluating hit targets after pseudo-element changes.
   */
  #reconcilePointerTarget() {
    const { x, y } = this.#lastPointer;
    requestAnimationFrame(() => {
      const target = document.elementFromPoint(x, y);
      if (!target) return;
      const listItem = target.closest(".menu-list__list-item");
      if (listItem && !listItem.contains(this.#state.activeItem)) {
        listItem.dispatchEvent(
          new PointerEvent("pointerenter", { bubbles: false }),
        );
      }
    });
  }

  /**
   * Begin pointer tracking for the safety box on the newly active item.
   * @param {HTMLElement} item
   * @param {HTMLElement | null} previousItem
   */
  #startPointerTracking(item, previousItem) {
    if (previousItem) {
      this.#stopPointerTracking(previousItem);
    } else {
      document.body.addEventListener("pointermove", this.#onPointerMove);
    }

    const rect = item.getBoundingClientRect();
    const isOverlap = this.headerComponent?.hasAttribute(
      "data-submenu-overlap-bottom-row",
    );
    const boundary = isOverlap
      ? this.headerComponent?.querySelector(".header__row--top")
      : this.headerComponent;
    const submenu = findSubmenu(item);
    const bridgeBottom = Math.max(
      boundary?.getBoundingClientRect().bottom ?? 0,
      submenu?.getBoundingClientRect().bottom ?? 0,
    );
    item.style.setProperty("--box-height", `${bridgeBottom - rect.top}px`);
  }

  /**
   * True when the pointer is still over the trigger, this menu, or its submenu.
   * Used so a spurious pointerleave (pill underlay / clip-path gap) does not
   * close the mega menu while the cursor is still on Shop Products.
   * @returns {boolean}
   */
  #isPointerOverOpenMenu() {
    const item = this.#state.activeItem;
    if (!item) return false;

    const listItem = item.closest(".menu-list__list-item");
    const submenu = findSubmenu(item);

    if (item.matches(":hover")) return true;
    if (listItem instanceof HTMLElement && listItem.matches(":hover")) return true;
    if (submenu?.matches(":hover")) return true;
    if (this.matches(":hover")) return true;
    if (this.overflowListHovered || this.overflowMenu?.matches(":hover")) return true;

    const { x, y } = this.#lastPointer;
    if (!x && !y) return false;

    const el = document.elementFromPoint(x, y);
    if (!(el instanceof Element)) return false;

    return Boolean(
      this.contains(el) || listItem?.contains(el) || submenu?.contains(el),
    );
  }

  /**
   * Align the submenu clip-path with the real bottom of the nav row so the
   * pill header's 76px --header-height does not leave a dead hover gap.
   */
  #syncSubmenuClipTop() {
    const item = this.#state.activeItem;
    const submenu = findSubmenu(item);
    const row = item?.closest(".header__row");
    if (!submenu || !row || !this.headerComponent) return;

    const clipTop =
      row.getBoundingClientRect().bottom - submenu.getBoundingClientRect().top;
    this.headerComponent.style.setProperty(
      "--submenu-clip-top",
      `${Math.max(0, clipTop)}px`,
    );
  }

  /**
   * Stop pointer tracking and remove all safety box properties from an item.
   * @param {HTMLElement} item
   */
  #stopPointerTracking(item) {
    clearTimeout(this.#pointerIdleTimer);
    this.#pointerIdleTimer = undefined;
    item.style.removeProperty("--box-height");
    delete item.dataset.safetyBox;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (
      this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]')
    );
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return (
      this.refs.overflowMenu?.shadowRoot
        ?.querySelector('[part="overflow-list"]')
        ?.matches(":hover") ?? false
    );
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest("header-component"));
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    // Pointer is back over the menu — cancel any pending grace-period close.
    clearTimeout(this.#closeTimer);

    if (!(event.target instanceof Element) || !this.headerComponent) return;

    if (event instanceof PointerEvent) {
      this.#lastPointer.x = event.clientX;
      this.#lastPointer.y = event.clientY;
    }

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    // Only reset the sidebar to its default panel when a menu genuinely opens.
    this.dispatchEvent(new MegaMenuHoverEvent());

    const isDefaultSlot = event.target.slot === "";

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = "false";
    }

    this.#state.activeItem = item;
    this.ariaExpanded = "true";
    item.ariaExpanded = "true";

    let submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = "";

      // Cleanup any existing mutation observer from previous menu activations
      this.#cleanupMutationObserver();

      // Monitor DOM mutations to catch deferred content injection (from section hydration)
      this.#submenuMutationObserver = new MutationObserver(() => {
        requestAnimationFrame(() => {
          // Double requestAnimationFrame to ensure the height is properly calculated and not defaulting to the contain-intrinsic-size
          requestAnimationFrame(() => {
            if (submenu.offsetHeight > 0) {
              this.headerComponent?.style.setProperty(
                "--submenu-height",
                `${submenu.offsetHeight}px`,
              );
              this.#cleanupMutationObserver();
            }
          });
        });
      });
      this.#submenuMutationObserver.observe(submenu, {
        childList: true,
        subtree: true,
      });

      // Auto-disconnect after 500ms to prevent memory leaks
      setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 500);
    }

    let finalHeight = submenu?.offsetHeight || 0;

    // For overflow menu, the height needs to be either content of the submenu or the total height of the menu list links
    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        /* Note: When the submenu is inside the overflow menu, its offsetHeight is not valid due to the lack of padding
         * we could add the padding variables to the submenu.offsetHeight, but measuring the overflowMenu.offsetHeight is just easier */
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    if (!submenu) {
      // If there is no content to open, don't try to open it
      finalHeight = 0;
    }

    this.headerComponent.style.setProperty(
      "--submenu-height",
      `${finalHeight}px`,
    );
    this.#setFullOpenHeaderHeight(finalHeight);
    this.#observeSubmenuResize(isDefaultSlot && hasSubmenu ? submenu : null);
    this.style.setProperty("--submenu-opacity", "1");
    this.#startPointerTracking(item, previouslyActiveItem);
    requestAnimationFrame(() => this.#syncSubmenuClipTop());
  };

  /**
   * Keep `--submenu-height` / `--full-open-header-height` in sync with the active
   * submenu's actual rendered height, so the underlay card fits the content even
   * when it changes after opening (sidebar panel switches, image/font loads, etc.).
   * @param {HTMLElement | null | undefined} submenu
   */
  #observeSubmenuResize(submenu) {
    this.#submenuResizeObserver?.disconnect();
    if (!submenu) return;

    this.#submenuResizeObserver = new ResizeObserver(() => {
      // Only the default-slot submenus drive the card height this way; overflow
      // menus are measured separately in `activate`.
      this.recalculateActiveSubmenuHeight();
      this.#syncSubmenuClipTop();
    });
    this.#submenuResizeObserver.observe(submenu);
  }

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    const menu = findSubmenu(this.#state.activeItem);
    const relatedTarget =
      event.relatedTarget instanceof Node ? event.relatedTarget : null;

    // Keyboard only: focus moved to another element still inside the menu → keep open.
    // (Checked only for `blur`; `document.activeElement` is stale after a mouse click
    // on an in-menu control, so relying on it for pointer leaves kept the menu open
    // even when the pointer had left entirely.)
    const isKeepingFocusWithinMenu =
      event.type === "blur" && Boolean(menu?.contains(document.activeElement));
    // Pointer or keyboard: heading into the submenu itself → keep open.
    const isMovingToSubmenu = Boolean(
      relatedTarget && menu?.contains(relatedTarget),
    );
    const isMovingToOverflowMenu = Boolean(
      relatedTarget?.parentElement?.matches('[slot="overflow"]'),
    );
    const isMovingWithinMenu = Boolean(
      relatedTarget && this.contains(relatedTarget),
    );

    if (
      isKeepingFocusWithinMenu ||
      isMovingToOverflowMenu ||
      isMovingToSubmenu ||
      isMovingWithinMenu
    ) {
      if (this.#state.activeItem) {
        this.#stopPointerTracking(this.#state.activeItem);
      }
      return;
    }

    // Close after a short grace period rather than immediately. Moving the pointer
    // from a top-level item down into its submenu briefly crosses a gap where it's
    // over neither element — closing instantly there is what forced users to move
    // fast to keep the menu open. #deactivate bails if the pointer has since landed
    // on the submenu, and activate() cancels this timer if a menu re-opens.
    clearTimeout(this.#closeTimer);
    this.#closeTimer = setTimeout(() => this.#deactivate(), 200);
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    clearTimeout(this.#closeTimer);
    if (!item || item != this.#state.activeItem) return;

    const submenu = findSubmenu(item);

    // Don't deactivate if the pointer is still over the trigger, menu, or submenu.
    if (this.#isPointerOverOpenMenu()) return;

    this.#submenuResizeObserver?.disconnect();
    this.#submenuResizeObserver = null;

    this.headerComponent?.style.setProperty("--submenu-height", "0px");
    this.#setFullOpenHeaderHeight(0);
    this.style.setProperty("--submenu-opacity", "0");
    this.dataset.overflowExpanded = "false";

    document.body.removeEventListener("pointermove", this.#onPointerMove);
    this.#stopPointerTracking(item);

    this.#state.activeItem = null;
    this.ariaExpanded = "false";
    item.ariaExpanded = "false";

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu
      ?.querySelector("slot")
      ?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (
          link.querySelector('[ref="submenu[]"]')
        );
        if (submenu) {
          cb(submenu);
        }
      });
    };

    mapSubmenus((submenu) => {
      submenu.style.setProperty("display", "none");
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty("display");
    });
    return height;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   */
  #setFullOpenHeaderHeight(submenuHeight) {
    if (!this.headerComponent) return;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen
      ? 0
      : submenuHeight + this.#getMenuRowOffset();

    this.headerComponent.style.setProperty(
      "--full-open-header-height",
      `${fullOpenHeaderHeight}px`,
    );
  }

  /**
   * The vertical distance from the top of the header to where the open submenu
   * content begins (the bottom edge of the row the active menu lives in). Adding
   * this to the submenu height makes the underlay card end exactly at the content,
   * with no leftover space — regardless of how many header rows/paddings exist.
   * @returns {number}
   */
  #getMenuRowOffset() {
    if (!this.headerComponent) return 0;

    const row = this.#state.activeItem?.closest(".header__row");
    if (row) {
      const offset =
        row.getBoundingClientRect().bottom -
        this.headerComponent.getBoundingClientRect().top;
      if (offset > 0) return offset;
    }

    // Fallback to the previous heuristic if the row can't be resolved.
    const isOverlapSituation = this.headerComponent.hasAttribute(
      "data-submenu-overlap-bottom-row",
    );
    return isOverlapSituation && this.headerComponent.offsetHeight > 0
      ? /** @type {HTMLElement | null} */ ((
          this.headerComponent.querySelector(".header__row--top")
        )?.offsetHeight ?? 0)
      : this.headerComponent.offsetHeight;
  }

  /**
   * Recalculate the active submenu's height. Call this when the submenu's content
   * changes size after it was opened (e.g. switching sidebar mega-menu panels),
   * so the underlay card and clip-path resize to fit the currently visible panel.
   */
  recalculateActiveSubmenuHeight() {
    const item = this.#state.activeItem;
    if (!item || !this.headerComponent) return;

    const submenu = findSubmenu(item);
    if (!submenu) return;

    const finalHeight = submenu.offsetHeight || 0;
    this.headerComponent.style.setProperty(
      "--submenu-height",
      `${finalHeight}px`,
    );
    this.#setFullOpenHeaderHeight(finalHeight);
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute("loading"));
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get("header-menu")) {
  customElements.define("header-menu", HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(
      element.parentElement?.querySelector('[slot="overflow"]'),
    );
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
